import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useMainCompany } from "../../context/MainCompanyContext";
import { useRoleTheme } from "../../context/RoleThemeContext";
import api from "../../api/axios";
import Modal from "../../components/common/AdminModal";
import * as yup from "yup";
import {
  Briefcase,
  Send,
  ShieldOff,
  KeyRound,
  Pencil,
  Trash,
  FileSpreadsheet,
  RefreshCw,
  CheckCircle,
  XCircle,
  Building2,
  User,
  UserCheck,
  Mail,
  Phone,
  MapPin,
  Lock,
  Settings,
  ZoomIn
} from "lucide-react";
import AdminPageHeader from "../../components/common/AdminPageHeader";
import Toast from "../../components/common/Toast";

// Debounce hook removed - using manual search now

// ✅ Admin Add User Validation Schema
const adminAddUserSchema = yup.object().shape({
  first_name: yup
    .string()
    .trim()
    .required("First name is required"),
  last_name: yup
    .string()
    .trim()
    .required("Last name is required"),
  email: yup
    .string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  username: yup
    .string()
    .required("Username is required")
    .min(6, "Username must be at least 6 characters"),
  password: yup
    .string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/\d/, "Password must contain at least one number")
    .matches(/[=!\-@._&%$#?'"<>^*]/, "Password must contain at least one special character"),
  user_type: yup
    .string()
    .required("User type is required"),
  mailing_address: yup
    .string()
    .trim()
    .required("Address is required"),
  city: yup
    .string()
    .matches(/^[a-zA-Z \-']+$/, "City can only contain letters, spaces, hyphens, and apostrophes")
    .required("City is required"),
  state: yup
    .string()
    .required("State is required"),
  county: yup
    .string()
    .required("County is required"),
  postal_code: yup
    .string()
    .required("ZIP code is required"),
  phone: yup
    .string()
    .required("Phone number is required")
    .test("phone-length", "Please enter a valid phone number", (value) => {
      if (!value) return false;
      const digits = value.replace(/[^0-9]/g, "");
      return digits.length >= 10 && digits.length <= 12;
    }),
  office_name: yup
    .string()
    .required("Brokerage name is required"),
});

const emptyEditAdditionalData = {
  mailing_address: "",
  city: "",
  postal_code: "",
  phone: "",
  office_name: "",
  state: "",
  county: "",
  titlerep: "",
};

const adminEditAdditionalSchema = yup.object().shape({
  mailing_address: yup
    .string()
    .trim()
    .required("Address is required"),
  city: yup
    .string()
    .matches(/^[a-zA-Z \-']+$/, "City can only contain letters, spaces, hyphens, and apostrophes")
    .required("City is required"),
  state: yup
    .string()
    .required("State is required"),
  county: yup
    .string()
    .required("County is required"),
  postal_code: yup
    .string()
    .required("ZIP code is required"),
  phone: yup
    .string()
    .required("Phone number is required")
    .test("phone-length", "Please enter a valid phone number", (value) => {
      if (!value) return false;
      const digits = value.replace(/[^0-9]/g, "");
      return digits.length >= 10 && digits.length <= 12;
    }),
  office_name: yup
    .string()
    .required("Brokerage name is required"),
});

const mapYupErrors = (validationError) => {
  const errors = {};
  validationError.inner?.forEach((err) => {
    if (err.path && !errors[err.path]) {
      errors[err.path] = err.message;
    }
  });
  return errors;
};

const buildAdminEditProfileSchema = () =>
  adminEditAdditionalSchema.shape({
    first_name: yup.string().trim().required("First name is required"),
    last_name: yup.string().trim().required("Last name is required"),
    email: yup
      .string()
      .email("Please enter a valid email address")
      .required("Email is required"),
    confirm_email: yup
      .string()
      .required("Confirm email is required")
      .oneOf([yup.ref("email")], "Email and Confirm Email do not match"),
    changePassword: yup.boolean(),
    changeUsername: yup.boolean(),
    password: yup.string().when("changePassword", {
      is: true,
      then: (schema) =>
        schema
          .required("Password is required")
          .min(6, "Password must be 6–15 characters")
          .max(15, "Password must be 6–15 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    confirm_password: yup.string().when("changePassword", {
      is: true,
      then: (schema) =>
        schema
          .required("Confirm password is required")
          .oneOf(
            [yup.ref("password")],
            "Password and Confirm Password do not match",
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    username: yup.string().when("changeUsername", {
      is: true,
      then: (schema) =>
        schema.trim().required("Username is required when changing username"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

const editInputBase = "w-full border rounded-lg px-3 py-2 text-sm transition-colors focus:ring-2";

const getEditInputClass = (field, errors, focusClass) =>
  `${editInputBase} ${errors[field]
    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
    : `border-gray-300 ${focusClass}`
  }`;

// ✅ Generic Confirm Modal

export default function UsersList() {
  const { selectedMain, mainCompanies } = useMainCompany();
  const { roleTheme } = useRoleTheme();
  const primaryBtnClasses = `${roleTheme?.colors?.buttons?.primary || 'bg-blue-600 hover:bg-blue-700'} text-white`;
  // console.log("mainCompanies", mainCompanies);
  // console.log("selectedMain", selectedMain);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);

  const [modal, setModal] = useState({ type: null, data: null });
  const [step, setStep] = useState("confirm"); // confirm → final

  const [, setSuccessMessage] = useState("");
  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [reps, setReps] = useState([]); // store fetched reps
  const [selectedRep, setSelectedRep] = useState("");
  const [sendEmail, setSendEmail] = useState("");

  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editConfirmEmail, setEditConfirmEmail] = useState("");

  const [changePassword, setChangePassword] = useState(false);
  const [editPassword, setEditPassword] = useState("");
  const [editConfirmPassword, setEditConfirmPassword] = useState("");

  const [changeUsername, setChangeUsername] = useState(false);
  const [editUsername, setEditUsername] = useState("");
  const [formError, setFormError] = useState("");
  const [editFormErrors, setEditFormErrors] = useState({});
  const [editUserDetails, setEditUserDetails] = useState(null);
  const [editUserProfilePreviewOpen, setEditUserProfilePreviewOpen] = useState(false);
  const [editUserProfilePicBust, setEditUserProfilePicBust] = useState(0);
  const [editAdditionalData, setEditAdditionalData] = useState(emptyEditAdditionalData);
  const [editUserStates, setEditUserStates] = useState([]);
  const [editUserCounties, setEditUserCounties] = useState([]);
  const [editUserTitleReps, setEditUserTitleReps] = useState([]);
  const [isLoadingEditCounties, setIsLoadingEditCounties] = useState(false);

  const [filters, setFilters] = useState({
    query: "",
    createdFrom: "",
    createdTo: "",
    userType: "",
  });
  // Keep filters for dates and other controls, but debounce the search query separately
  // Search input for manual search
  const [searchQuery, setSearchQuery] = useState(filters.query || "");
  const location = useLocation();
  const navigate = useNavigate();

  // State & County dropdowns
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [selectedState, setSelectedState] = useState(0);
  const [selectedCounty, setSelectedCounty] = useState(0);
  const [titlereps, setTitleReps] = useState([]);
  const [selectedTitleRep, setSelectedTitleRep] = useState(0);
  // If the page is opened from a notification we may receive a userId in the URL
  const [urlUserId, setUrlUserId] = useState(null);

  // Filter input classes are now inline for better control

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10 });

  // Add User modal states
  const [addUserStates, setAddUserStates] = useState([]);
  const [addUserCounties, setAddUserCounties] = useState([]);
  const [addUserTitleReps, setAddUserTitleReps] = useState([]);
  const [addUserFormData, setAddUserFormData] = useState({
    user_type: "Normal User",
    first_name: "",
    last_name: "",
    email: "",
    username: "",
    password: "",
    mailing_address: "",
    city: "",
    state: "",
    county: "",
    postal_code: "",
    phone: "",
    office_name: "",
    titlerep: "",
    dre_nmls: "",
    corporate_nmls: ""
  });
  const [addUserErrors, setAddUserErrors] = useState({});
  const addUserEmailValidRef = useRef(null);
  const addUserUsernameValidRef = useRef(null);

  const modalConfig = {
    assign: {
      title: "Assign Sales Rep",
      confirm: "Are you sure you want to change Title Rep?",
      final: "Final confirmation: this will assign a representative immediately.",
      button: { text: "Yes, Assign", color: "bg-green-600" },
      action: "Assign Rep",
    },
    sendEmail: {
      title: "Resend Welcome Email",
      confirm: "Are you sure you want to send Welcome Email?",
      final: "Final confirmation: this will send emails immediately.",
      button: { text: "Yes, Send", color: "bg-green-600" },
      action: "Send Email",
    },
    edit: {
      title: "Edit User",
      confirm: "Are you sure you want to edit User Profile?",
      final:
        "Final confirmation: saving changes will update the user profile immediately.",
      button: { text: "Yes, Save", color: "bg-green-600" },
      action: "Edit Profile",
    },
    reset: {
      title: "Reset Password",
      confirm: "Are you sure you want to reset password for this user?",
      final: "Final confirmation: password will be reset immediately.",
      button: { text: "Yes, Reset", color: "bg-orange-600" },
      action: "Reset Password",
    },
    disable: (user) => ({
      title: user?.is_active ? "Deactivate User" : "Activate User",
      confirm: user?.is_active
        ? "Are you sure you want to deactivate this user profile?"
        : "Are you sure you want to activate this user profile?",
      final: user?.is_active
        ? "Final confirmation: this will disable user login immediately."
        : "Final confirmation: this will activate user login immediately.",
      button: {
        text: user?.is_active ? "Yes, Deactivate" : "Yes, Activate",
        color: "bg-yellow-600",
      },
      action: user?.is_active ? "Disable User" : "Activate User",
    }),
    delete: {
      title: "Delete User",
      confirm: (
        <>
          Are you sure you want to delete this user?
          <br />
          <b>This action cannot be undone.</b>
        </>
      ),
      final: "Final confirmation: user data will be permanently deleted.",
      button: { text: "Yes, Delete", color: "bg-red-600" },
      action: "Delete User",
    },
    approve: {
      title: "Approve User",
      confirm: "Are you sure you want to approve this user?",
      final: "Final confirmation: this will allow the user to log in.",
      button: { text: "Yes, Approve", color: "bg-green-600" },
      action: "Approve User",
    },
    reject: {
      title: "Reject User",
      confirm: "Are you sure you want to reject this user?",
      final: "Final confirmation: this will prevent the user from logging in.",
      button: { text: "Yes, Reject", color: "bg-red-600" },
      action: "Reject User",
    },
    addUser: {
      title: "Add New User",
      confirm: "Are you sure you want to add a new user?",
      final: "Final confirmation: this will create a new user account.",
      button: { text: "Yes, Add User", color: "bg-green-600" },
      action: "Add New User",
    },
  };

  // Helper to show toast messages consistently
  const showToast = (message, type = 'success', duration = 3000) => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), duration);
  };

  // Helper to show a simple informational modal (replaces alert())
  const showInfoModal = (title, message) => {
    setModal({ type: 'info', data: { title, message } });
  };


  // ✅ Put this above useEffect so it's reusable
  // Helper to format ISO date strings to 'Mon D, YYYY' (e.g., 'Sep 8, 2025')
  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const opts = { year: "numeric", month: "short", day: "numeric" };
      return d.toLocaleDateString(undefined, opts);
    } catch (e) {
      return iso.split("T")[0] || iso;
    }
  };
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        q: searchQuery || undefined,
        page,
        limit: pageSize,
        createdFrom: filters.createdFrom || undefined,
        createdTo: filters.createdTo || undefined,
        user_type: filters.userType || undefined,
        maincompanyid: selectedMain || undefined,
      };
      //   console.log('fetchUsers urlUserId:', urlUserId);
      // If a userId was passed via URL (e.g. from a notification), send it to the API
      if (urlUserId) params.userId = urlUserId;
      if (selectedState) params.state = selectedState;
      if (selectedCounty) params.county = selectedCounty;
      if (selectedTitleRep) params.titlerep = selectedTitleRep;

      const { data } = await api.get("/admin/users", { params });
      const mapped = (data?.data ?? []).map((u) => ({
        id: u.id,
        name: u.name,
        company: u.company_name,
        company_name: u.company_name,
        username: u.username,
        email: u.email,
        phone: u.phone || "",
        titleRep: u.title_rep || "",
        title_rep: u.title_rep,
        city: u.city || "",
        mailing_address: u.mailing_address || "",
        office_name: u.office_name || "",
        postal_code: u.postal_code || "",
        user_status: u.user_status || "",
        user_type: u.user_type || "",
        profile_pic: u.profile_pic ?? u.profilePic ?? "",
        created: formatDate(u.created) || "",
        state: u.state,
        county: u.county,
        is_active: u.is_active
      }));
      setUsers(mapped);
      setMeta(data?.meta || { total: mapped.length, page, limit: pageSize });
      setError(null);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Could not load users. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [selectedMain, page, pageSize, searchQuery, selectedState, selectedCounty, selectedTitleRep, urlUserId, filters]);


  // Page reset logic is now handled in the search button click

  // Initialize searchQuery from URL param `q` so other pages can link to a pre-filtered listing
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const uid = params.get('userId');
      // console.log('URL params:', { uid });
      if (uid) setUrlUserId(uid);
    } catch (e) {
      // ignore
    }
    // only run on mount / when location.search changes
  }, [location.search]);

  // ✅ Fetch users when filters or pagination change
  // Manual search: searchQuery/filters only trigger fetch when Search is clicked.
  // Pagination: refetch when page or pageSize changes.
  useEffect(() => {
    // console.log('fetchUsers useEffect');
    fetchUsers();
    // Intentionally omit fetchUsers to avoid refetch on callback identity change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMain, page, pageSize, urlUserId]);



  // When the selected main company changes in the layout header, refresh the listing.
  // Also reset pagination and clear any selection/modal state to avoid stale actions.
  useEffect(() => {
    // reset pagination and UI state
    setPage(1);
    setSelected([]);
    setModal({ type: null, data: null });
    // When selectedMain changes we rely on the fetch useEffect (which now depends on selectedMain)
    // to perform the network request. Here we only reset local UI state to avoid stale actions.
  }, [selectedMain]);

  // Deep link support: opened with ?userId=<id> (e.g. from the combined Users/Sales
  // Reps list) — open straight to the edit modal's final step, skipping the manual
  // "are you sure" confirm step. Declared AFTER the selectedMain-reset effect above
  // so it wins when both fire in the same render (first mount, when selectedMain
  // settles from undefined to a real value at the same time urlUserId is read from
  // the URL) — otherwise that reset would immediately close the modal opened here.
  //
  // Fetches the full record first and opens with a list-row-shaped object (not just
  // {id}) because a separate effect (keyed on `modal`) seeds editFirstName/editLastName/
  // editEmail/editUsername directly from modal.data[0].name/.email/.username, and never
  // re-syncs them once the later detail-fetch effect populates editUserDetails — so a
  // bare {id} stub would leave those basic fields permanently blank.
  const deepLinkUserIdRef = useRef(null);
  useEffect(() => {
    if (!urlUserId || !selectedMain) return;
    if (deepLinkUserIdRef.current === urlUserId) return;
    deepLinkUserIdRef.current = urlUserId;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${urlUserId}`, {
          params: { maincompanyid: selectedMain },
        });
        const user = data?.data ?? data;
        if (cancelled || !user || !user.id) return;
        const firstName = user.first_name ?? user.firstname ?? "";
        const lastName = user.last_name ?? user.lastname ?? "";
        const name = user.name || `${firstName} ${lastName}`.trim();
        const constructed = { ...user, name };
        // Saving reads selected[0].id (the checkbox-selection state), not modal.data[0].id —
        // keep both in sync here the same way the normal "check row, click Edit" flow does.
        setSelected([constructed]);
        setModal({ type: "edit", data: [constructed] });
        setStep("final");
      } catch (e) {
        console.error("Failed to load user for edit deep link", e);
        showToast?.("Could not load that user.", "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [urlUserId, selectedMain]);

  // Load states on mount and whenever the selected main company changes
  useEffect(() => {
    const loadStates = async () => {
      try {
        // clear dependent selections when main company changes
        setSelectedState(0);
        setSelectedCounty(0);
        setSelectedTitleRep(0);
        setTitleReps([]);

        const params = {};
        if (selectedMain) params.maincompanyid = selectedMain;
        const { data } = await api.get('/admin/states', { params });
        const items = Array.isArray(data) ? data : data?.data || [];
        setStates(items);
      } catch (e) {
        console.error('Failed loading states', e?.response?.data || e.message || e);
        setStates([]);
      }
    };
    loadStates();
  }, [selectedMain]);

  // Load counties when selectedState changes
  useEffect(() => {
    const load = async () => {
      if (!selectedState) return setCounties([]);
      try {
        const params = { stateId: selectedState };
        if (selectedMain) params.maincompanyid = selectedMain;
        const { data } = await api.get('/auth/counties', { params });
        const items = Array.isArray(data) ? data : data?.data || [];
        setCounties(items);
      } catch (e) {
        console.error('Failed loading counties', e?.response?.data || e.message || e);
        setCounties([]);
      }
    };
    // clear previous county when state changes
    setSelectedCounty(0);
    load();
    // also clear title rep and titlereps when state changes
    setSelectedTitleRep(0);
    setTitleReps([]);
  }, [selectedState, selectedMain]);

  // Load title reps only when the county changes.
  // Avoid calling the titlereps API on initial page load — only fetch when a county is selected/changed.
  useEffect(() => {
    // clear selectedTitleRep when county changes then load
    setSelectedTitleRep(0);
    const load = async () => {
      // Delegate to shared fetchTitleReps (defined below)
      if (!selectedCounty) {
        setTitleReps([]);
        return;
      }
      const items = await fetchTitleReps();
      setTitleReps(items);
    };
    load();
    // Intentionally omit fetchTitleReps to avoid refetch on callback identity change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCounty, modal, selectedState]);

  // Load states for add user modal
  useEffect(() => {
    const loadAddUserStates = async () => {
      if (modal.type !== 'addUser') return;
      try {
        const params = {};
        if (selectedMain) params.maincompanyid = selectedMain;
        const { data } = await api.get('/admin/states', { params });
        const items = Array.isArray(data) ? data : data?.data || [];
        setAddUserStates([{ id: "", statename: "Select State" }, ...items]);
      } catch (e) {
        console.error('Failed loading add user states', e?.response?.data || e.message || e);
        setAddUserStates([]);
      }
    };
    loadAddUserStates();
  }, [modal.type, selectedMain]);

  // Load counties for add user modal
  useEffect(() => {
    const loadAddUserCounties = async () => {
      if (modal.type !== 'addUser' || !addUserFormData.state) {
        setAddUserCounties([]);
        return;
      }
      try {
        const params = { stateId: addUserFormData.state };
        if (selectedMain) params.maincompanyid = selectedMain;
        const { data } = await api.get('/auth/counties', { params });
        const items = Array.isArray(data) ? data : data?.data || [];
        setAddUserCounties([{ id: "", countyname: "Select County" }, ...items]);
      } catch (e) {
        console.error('Failed loading add user counties', e?.response?.data || e.message || e);
        setAddUserCounties([]);
      }
    };
    loadAddUserCounties();
  }, [modal.type, addUserFormData.state, selectedMain]);

  // Load title reps for add user modal
  useEffect(() => {
    const loadAddUserTitleReps = async () => {
      if (modal.type !== 'addUser' || !addUserFormData.state || !addUserFormData.county) {
        setAddUserTitleReps([]);
        return;
      }
      try {
        const params = {
          stateId: addUserFormData.state,
          countyId: addUserFormData.county
        };
        if (selectedMain) params.maincompanyid = selectedMain;
        const { data } = await api.get('/auth/titlereps', { params });
        const items = Array.isArray(data) ? data : data?.data || [];
        setAddUserTitleReps([{ id: "", name: "Select Title Rep" }, ...items]);
      } catch (e) {
        console.error('Failed loading add user title reps', e?.response?.data || e.message || e);
        setAddUserTitleReps([]);
      }
    };
    loadAddUserTitleReps();
  }, [modal.type, addUserFormData.state, addUserFormData.county, selectedMain]);

  // Centralized title reps fetcher used by multiple effects (returns items)
  const fetchTitleReps = useCallback(async ({ stateIdParam, countyIdParam } = {}) => {
    try {
      const sId = stateIdParam || selectedState;
      const cId = countyIdParam || selectedCounty;

      // Keep original behavior: if no county is available, return empty
      if (!cId) return [];

      const params = {};
      if (sId) params.stateId = sId;
      if (cId) params.countyId = cId;
      if (selectedMain) params.maincompanyid = selectedMain;

      const { data } = await api.get('/auth/titlereps', { params });
      const items = Array.isArray(data) ? data : data?.data || [];
      return items;
    } catch (e) {
      console.error('Failed fetching title reps', e?.response?.data || e.message || e);
      return [];
    }
  }, [selectedMain, selectedState, selectedCounty]);

  useEffect(() => {
    //   console.log("Modal changed:", modal);
    if (modal.type === "sendEmail") {
      if (Array.isArray(modal.data)) {
        setSendEmail(modal.data[0]?.email || "");
      } else {
        setSendEmail(modal.data?.email || "");
      }
    }
  }, [modal]);

  useEffect(() => {
    if (modal && modal.type === "edit" && modal.data?.[0]) {
      const user = modal.data[0]; // selected row data

      // ✅ Split full name into first + last
      let firstName = "";
      let lastName = "";
      if (user.name) {
        const parts = user.name.trim().split(" ");
        firstName = parts.shift() || "";
        lastName = parts.join(" ") || ""; // handles middle/last combined
      }

      setEditFirstName(firstName);
      setEditLastName(lastName);
      setEditEmail(user.email || "");
      setEditConfirmEmail(user.email || "");
      setEditUsername(user.username || "");

      // reset optional states
      setChangePassword(false);
      setEditPassword("");
      setEditConfirmPassword("");
      setChangeUsername(false);
      setEditUserDetails(null);
      setEditAdditionalData(emptyEditAdditionalData);
      setEditUserStates([]);
      setEditUserCounties([]);
      setEditUserTitleReps([]);
      setEditFormErrors({});
      setFormError("");
    }
  }, [modal]);

  const clearEditFieldError = (field) => {
    setEditFormErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Fetch full user details when edit modal reaches final step (for city, mailing_address, etc.)
  useEffect(() => {
    if (modal?.type !== "edit" || step !== "final") return;
    const userId = modal.data?.[0]?.id;
    if (!userId || !selectedMain) return;

    let cancelled = false;
    const fetchUserDetails = async () => {
      try {
        const { data } = await api.get(`/admin/users/${userId}`, {
          params: { maincompanyid: selectedMain },
        });
        const user = data?.data ?? data;
        if (!cancelled && user && user.id) setEditUserDetails(user);
      } catch {
        if (!cancelled) setEditUserDetails(null);
      }
    };
    fetchUserDetails();
    return () => { cancelled = true; };
  }, [modal?.type, modal?.data, step, selectedMain]);

  useEffect(() => {
    if (!editUserDetails) return;
    const user = editUserDetails;
    setEditAdditionalData({
      mailing_address: user.mailing_address || "",
      city: user.city || "",
      postal_code: user.postal_code || "",
      phone: user.phone ? formatMobile(String(user.phone)) : "",
      office_name: user.office_name || "",
      state: user.stateid != null ? String(user.stateid) : user.state != null ? String(user.state) : "",
      county: user.countyid != null ? String(user.countyid) : user.county != null ? String(user.county) : "",
      titlerep: user.titlerep != null ? String(user.titlerep) : "",
    });
  }, [editUserDetails]);

  // Load states for edit user modal
  useEffect(() => {
    const loadEditUserStates = async () => {
      if (modal.type !== "edit" || step !== "final") return;
      try {
        const params = {};
        if (selectedMain) params.maincompanyid = selectedMain;
        const { data } = await api.get("/admin/states", { params });
        const items = Array.isArray(data) ? data : data?.data || [];
        setEditUserStates([{ id: "", statename: "Select State" }, ...items]);
      } catch (e) {
        console.error("Failed loading edit user states", e?.response?.data || e.message || e);
        setEditUserStates([]);
      }
    };
    loadEditUserStates();
  }, [modal.type, step, selectedMain]);

  // Load counties for edit user modal
  useEffect(() => {
    const loadEditUserCounties = async () => {
      if (modal.type !== "edit" || step !== "final" || !editAdditionalData.state) {
        setEditUserCounties([]);
        return;
      }
      setIsLoadingEditCounties(true);
      try {
        const params = { stateId: editAdditionalData.state };
        if (selectedMain) params.maincompanyid = selectedMain;
        const { data } = await api.get("/auth/counties", { params });
        const items = Array.isArray(data) ? data : data?.data || [];
        setEditUserCounties([{ id: "", countyname: "Select County" }, ...items]);
      } catch (e) {
        console.error("Failed loading edit user counties", e?.response?.data || e.message || e);
        setEditUserCounties([]);
      } finally {
        setIsLoadingEditCounties(false);
      }
    };
    loadEditUserCounties();
  }, [modal.type, step, editAdditionalData.state, selectedMain]);

  // Load title reps for edit user modal
  useEffect(() => {
    const loadEditUserTitleReps = async () => {
      if (
        modal.type !== "edit" ||
        step !== "final" ||
        !editAdditionalData.state ||
        !editAdditionalData.county
      ) {
        setEditUserTitleReps([]);
        return;
      }
      try {
        const params = {
          stateId: editAdditionalData.state,
          countyId: editAdditionalData.county,
        };
        if (selectedMain) params.maincompanyid = selectedMain;
        const { data } = await api.get("/auth/titlereps", { params });
        const items = Array.isArray(data) ? data : data?.data || [];
        setEditUserTitleReps([{ id: "", name: "Select Title Rep" }, ...items]);
      } catch (e) {
        console.error("Failed loading edit user title reps", e?.response?.data || e.message || e);
        setEditUserTitleReps([]);
      }
    };
    loadEditUserTitleReps();
  }, [
    modal.type,
    step,
    editAdditionalData.state,
    editAdditionalData.county,
    selectedMain,
  ]);

  const getEditFlowUser = useCallback(() => {
    if (modal.type !== 'edit' || !modal.data?.[0]) return null;
    const selectedUser = modal.data[0];
    const baseUser = users.find((u) => u.id === selectedUser.id) || selectedUser;
    return editUserDetails ? { ...baseUser, ...editUserDetails } : baseUser;
  }, [modal.type, modal.data, users, editUserDetails]);

  useEffect(() => {
    if (modal.type !== 'edit' || step !== 'final') setEditUserProfilePreviewOpen(false);
  }, [modal.type, step]);

  useEffect(() => {
    const loadIfAssign = async () => {
      if (modal.type !== 'assign' || !modal.data?.length) return;
      const user = modal.data[0];
      const items = await fetchTitleReps({ stateIdParam: user.state, countyIdParam: user.county });
      setReps(items || []);
    };

    loadIfAssign();
    // Intentionally omit fetchTitleReps to avoid refetch on callback identity change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.type, modal.data]); // 👈 add modal.data so it refreshes when county/state changes


  // Server-side pagination
  const totalPages = Math.max(1, Math.ceil((meta.total || 0) / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  const paginatedUsers = users; // server already returns the current page

  // ✅ Checkbox logic
  const toggleSelect = (user) => {
    const exists = selected.find(
      (item) =>
        item.id === user.id && item.state === user.state && item.county === user.county && item.is_active === user.is_active && item.user_status === user.user_status && item.user_type === user.user_type && item.email === user.email && item.name === user.name
    );

    if (exists) {
      setSelected(
        selected.filter(
          (item) =>
            !(
              item.id === user.id &&
              item.state === user.state &&
              item.county === user.county &&
              item.is_active === user.is_active &&
              item.user_status === user.user_status &&
              item.user_type === user.user_type &&
              item.email === user.email &&
              item.name === user.name
            )
        )
      );
    } else {
      setSelected([...selected, { id: user.id, state: user.state, county: user.county, is_active: user.is_active, user_status: user.user_status, user_type: user.user_type, email: user.email, name: user.name, username: user.username, profile_pic: user.profile_pic ?? user.profilePic ?? "" }]);
    }
  };


  const toggleSelectAll = () => {
    if (selected.length === paginatedUsers.length) {
      setSelected([]);
    } else {
      setSelected(
        // store the same shape used by single-toggle so comparisons match
        paginatedUsers.map((u) => ({ id: u.id, state: u.state, county: u.county, is_active: u.is_active, user_status: u.user_status, user_type: u.user_type, email: u.email, name: u.name, username: u.username, profile_pic: u.profile_pic ?? u.profilePic ?? "" }))
      );
    }
  };

  // The export endpoint has, on at least one confirmed production response (for the
  // combined accounts export), returned a file url as http:// despite the page itself
  // being served over https:// — Chrome silently blocks that download (mixed-content
  // download blocking) with no console error. Upgrading the scheme here is a stop-gap
  // for that backend inconsistency, not a substitute for fixing it server-side.
  const upgradeToHttps = (url) => {
    if (window.location.protocol === 'https:' && url?.startsWith('http://')) {
      return 'https://' + url.slice('http://'.length);
    }
    return url;
  };

  const handleExport = async () => {
    // Build params object so axios can serialize and attach token via interceptor
    const params = {
      // Use current search query for export
      q: searchQuery || "",
      titlerep: selectedTitleRep || "",
      state: selectedState || "",
      county: selectedCounty || "",
      user_type: filters.userType || "",
      maincompanyid: selectedMain || "",
    };

    // Fast path: try JSON response first (server may return { data: { url } })
    try {
      const respJson = await api.get(`/admin/users/export`, { params });
      const d = respJson?.data;
      if (d) {
        // support shapes: { url }  OR { data: { url } } OR { status, data: { url } }
        const url = (d.url) || (d.data && d.data.url) || (d?.data?.url);
        if (url) {
          window.open(upgradeToHttps(url), '_blank');
          return;
        }
      }
      // If we got here, server didn't return a URL JSON — fall through to binary attempt
    } catch (e) {
      // If server returned binary, axios may throw when trying to parse as JSON. We'll fall back.
      // swallow and continue to binary request below
    }

    // Fallback: request as arraybuffer to handle binary file responses
    try {
      const resp = await api.get(`/admin/users/export`, { params, responseType: 'arraybuffer' });
      const contentType = resp.headers['content-type'] || '';

      // Try to parse JSON anyway (some servers return JSON with arraybuffer)
      try {
        const text = new TextDecoder('utf-8').decode(resp.data || new Uint8Array());
        const parsed = JSON.parse(text);
        const url = (parsed && (parsed.url || (parsed.data && parsed.data.url)));
        if (url) {
          window.open(upgradeToHttps(url), '_blank');
          return;
        }
      } catch (e) {
        // not JSON — continue to treat as binary file
      }

      const blob = new Blob([resp.data], { type: contentType || 'application/octet-stream' });
      let filename = 'export.xlsx';
      const cd = resp.headers['content-disposition'] || resp.headers['Content-Disposition'];
      if (cd) {
        const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)/i);
        if (m && m[1]) {
          try { filename = decodeURIComponent(m[1]); } catch (err) { filename = m[1]; }
        }
      }
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(urlObj);
      return;
    } catch (err) {
      console.error('Export failed (binary fallback):', err);
      alert('Failed to export. Please try again.');
    }
  };

  // ✅ Final Action API Simulation
  const handleFinalAction = async (action) => {
    setActionLoading(true);
    let shouldResetStep = true; // Default to resetting step

    try {
      switch (action) {
        case "Assign Rep":
          //  console.log("Assigning", selectedRep, "to", selected);
          await api.post("/admin/users/" + selected[0].id + "/assign-sales-rep", {
            sales_rep_id: selectedRep
          });
          await fetchUsers(); // refresh list
          setSelected([]);
          setModal({ type: null, data: null }); // close popup and clear data
          break;
        case "Send Email":
          await api.post("/admin/users/" + selected[0].id + "/send-welcome-email", {
            email: sendEmail,
          });
          setSelected([]);
          setModal({ type: null, data: null }); // close popup and clear data
          break;
        case "Disable User":
        case "Activate User": // handle both with one block
          try {
            // prefer modal.data as the canonical user object (works for single or array)
            const usr = Array.isArray(modal.data) ? modal.data[0] : modal.data || selected[0];
            await api.post(`/admin/users/${usr.id}/toggle-status`, {
              is_active: !usr.is_active,
            });
            await fetchUsers(); // refresh list
            // clear selection so future modal opens use fresh users lookup
            setSelected([]);
            setModal({ type: null, data: null }); // close popup and clear data
          } catch (err) {
            console.error("Toggle status error:", err);
            throw err;
          }
          break;

        case "Approve User":
          await api.post(`/admin/users/${selected[0].id}/approve`);
          await fetchUsers(); // refresh list
          setSelected([]);
          setModal({ type: null, data: null }); // close popup and clear data
          break;

        case "Reject User":
          await api.post(`/admin/users/${selected[0].id}/reject`);
          await fetchUsers(); // refresh list
          setSelected([]);
          setModal({ type: null, data: null }); // close popup and clear data
          break;

        case "Add New User": {
          if (
            addUserEmailValidRef.current === false ||
            addUserUsernameValidRef.current === false
          ) {
            const errs = {};
            if (addUserEmailValidRef.current === false) {
              errs.email = "Email already exists.";
            }
            if (addUserUsernameValidRef.current === false) {
              errs.username = "Username already exists.";
            }
            setAddUserErrors((prev) => ({ ...prev, ...errs }));
            setFormError(
              Object.values(errs)[0] || "Please fix the highlighted fields.",
            );
            shouldResetStep = false;
            return;
          }

          try {
            await adminAddUserSchema.validate(addUserFormData, {
              abortEarly: false,
            });
          } catch (validationError) {
            if (validationError.name === "ValidationError") {
              const fieldErrors = mapYupErrors(validationError);
              setAddUserErrors(fieldErrors);
              setFormError(
                Object.values(fieldErrors)[0] ||
                "Please fix the highlighted fields.",
              );
              shouldResetStep = false;
              return;
            }
            throw validationError;
          }

          try {
            const zipRes = await api.post("/auth/verify-zip", {
              zipcode: addUserFormData.postal_code.trim(),
              maincompanyid: selectedMain,
              stateid: addUserFormData.state,
              countyid: addUserFormData.county,
            });
            if (zipRes.data?.status === "fail") {
              setAddUserErrors({ postal_code: "Invalid ZIP code" });
              setFormError("Invalid ZIP code");
              shouldResetStep = false;
              return;
            }
          } catch (err) {
            console.error("ZIP validation error:", err);
            setAddUserErrors({ postal_code: "ZIP code validation failed" });
            setFormError("ZIP code validation failed");
            shouldResetStep = false;
            return;
          }

          setAddUserErrors({});
          setFormError("");

          try {
            const stateObj = addUserStates.find(
              (s) => s.id === parseInt(addUserFormData.state),
            );
            const countyObj = addUserCounties.find(
              (c) => c.id === parseInt(addUserFormData.county),
            );

            const addUserPayload = {
              ...addUserFormData,
              mainid: selectedMain,
              gender: "M",
              state_name: stateObj?.statename,
              county_name: countyObj?.countyname,
              user_type:
                addUserFormData.user_type === "Sales Rep Manager" ||
                  addUserFormData.user_type === "SM"
                  ? "SM"
                  : addUserFormData.user_type === "Master Account" ||
                    addUserFormData.user_type === "M"
                    ? "M"
                    : "N",
            };

            const res = await api.post("/admin/users", addUserPayload);
            if (res.data.status === "success") {
              await fetchUsers();
              setAddUserFormData({
                user_type: "Normal User",
                first_name: "",
                last_name: "",
                email: "",
                username: "",
                password: "",
                mailing_address: "",
                city: "",
                state: "",
                county: "",
                postal_code: "",
                phone: "",
                office_name: "",
                titlerep: "",
                dre_nmls: "",
                corporate_nmls: "",
              });
              setModal({ type: null, data: null });
            } else {
              setFormError(res.data.message || "Failed to add user");
              shouldResetStep = false;
              return;
            }
          } catch (err) {
            console.error("Add New User error:", err);
            setFormError(
              err.response?.data?.message || "Unexpected error adding user.",
            );
            shouldResetStep = false;
            return;
          }
          break;
        }

        case "Reset Password":
          await api.post(`/admin/users/${selected[0].id}/reset-password`);
          setSelected([]);
          setModal({ type: null, data: null }); // close popup and clear data
          break;

        case "Edit Profile": {
          const editFormData = {
            first_name: editFirstName,
            last_name: editLastName,
            email: editEmail,
            confirm_email: editConfirmEmail,
            mailing_address: editAdditionalData.mailing_address,
            city: editAdditionalData.city,
            state: editAdditionalData.state,
            county: editAdditionalData.county,
            postal_code: editAdditionalData.postal_code,
            phone: editAdditionalData.phone,
            office_name: editAdditionalData.office_name,
            password: editPassword,
            confirm_password: editConfirmPassword,
            username: editUsername,
            changePassword,
            changeUsername,
          };

          try {
            await buildAdminEditProfileSchema().validate(editFormData, {
              abortEarly: false,
            });
          } catch (validationError) {
            if (validationError.name === "ValidationError") {
              const fieldErrors = mapYupErrors(validationError);
              setEditFormErrors(fieldErrors);
              setFormError(
                Object.values(fieldErrors)[0] ||
                "Please fix the highlighted fields.",
              );
              shouldResetStep = false;
              return;
            }
            throw validationError;
          }

          try {
            const zipRes = await api.post("/auth/verify-zip", {
              zipcode: editAdditionalData.postal_code.trim(),
              maincompanyid: selectedMain,
              stateid: editAdditionalData.state,
              countyid: editAdditionalData.county,
            });
            if (zipRes.data?.status === "fail") {
              setEditFormErrors({ postal_code: "Invalid ZIP code" });
              setFormError("Invalid ZIP code");
              shouldResetStep = false;
              return;
            }
          } catch (err) {
            console.error("ZIP validation error:", err);
            setEditFormErrors({ postal_code: "ZIP code validation failed" });
            setFormError("ZIP code validation failed");
            shouldResetStep = false;
            return;
          }

          setEditFormErrors({});
          setFormError("");

          //  const stateObj = editUserStates.find(
          //  (s) => String(s.id) === String(editAdditionalData.state),
          //      );
          //    const countyObj = editUserCounties.find(
          //      (c) => String(c.id) === String(editAdditionalData.county),
          //    );

          try {
            const payload = {
              first_name: editFirstName.trim(),
              last_name: editLastName.trim(),
              email: editEmail.trim(),
              mailing_address: editAdditionalData.mailing_address.trim(),
              city: editAdditionalData.city.trim(),
              state: editAdditionalData.state,
              county: editAdditionalData.county,
              //  state_name: stateObj?.statename,
              //   county_name: countyObj?.countyname,
              postal_code: editAdditionalData.postal_code.trim(),
              phone: editAdditionalData.phone.replace(/\D/g, ""),
              office_name: editAdditionalData.office_name.trim(),
              titlerep: editAdditionalData.titlerep || "",
            };
            if (changePassword) payload.password = editPassword;
            if (changeUsername) payload.username = editUsername.trim();

            const targetId = selected[0]?.id ?? modal.data?.[0]?.id;
            const res = await api.put(`/admin/users/${targetId}`, payload);

            if (res.data.status === "fail") {
              setFormError(res.data.message || "Failed to update user.");
              shouldResetStep = false;
              return;
            }

            setSuccessMessage(res.data.message || "Profile updated successfully!");
            setSelected([]);
            setModal({ type: null, data: null });
            setStep("confirm");
            setEditFormErrors({});
            await fetchUsers();
          } catch (err) {
            console.error("Edit Profile error:", err);
            setFormError(
              err.response?.data?.message || "Unexpected error updating user.",
            );
            shouldResetStep = false;
            return;
          }
          break;
        }

        case "Delete User":
          // Determine the list of user ids to delete.
          // Prefer modal.data (it may be an array of users or a single user),
          // otherwise fall back to the selected array from the table.
          try {
            let ids = [];
            if (Array.isArray(modal.data) && modal.data.length) {
              ids = modal.data.map((u) => u.id).filter(Boolean);
            } else if (modal.data && modal.data.id) {
              ids = [modal.data.id];
            } else if (Array.isArray(selected) && selected.length) {
              ids = selected.map((s) => s.id).filter(Boolean);
            }

            if (ids.length === 0) {
              throw new Error('No users selected for deletion');
            }

            if (ids.length === 1) {
              // single delete using DELETE /admin/users/:id
              await api.delete(`/admin/users/${ids[0]}`);
            } else {
              // bulk delete using POST /admin/users/bulk/delete
              await api.post('/admin/users/bulk/delete', { user_ids: ids });
            }

            await fetchUsers(); // refresh list
            setSelected([]);
            setModal({ type: null, data: null }); // close popup and clear data
          } catch (err) {
            // rethrow so outer catch handles the toast/error
            throw err;
          }
          break;
        default:
          console.warn("Unknown action:", action);
      }
      // show success toast
      showToast(`${action} successful!`, 'success');



    } catch (err) {
      console.error(`${action} error:`, err);
      showToast(err?.response?.data?.message || `Failed to ${action.toLowerCase()}.`, 'error');
    } finally {
      setActionLoading(false);
      // ensure modal resets step only on successful actions or actual errors, not validation errors
      if (shouldResetStep !== false) {
        setStep('confirm');
      }
    }
  };

  const showingFrom = meta.total ? start + 1 : 0;
  const showingTo = Math.min(end, meta.total || 0);

  // ✅ Name formatting helper (Title Case)
  const formatName = (name) => {
    if (!name) return "";
    return name
      .toLowerCase()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // ✅ Phone formatting helper
  const formatMobile = (input) => {
    const digits = input.replace(/[^0-9]/g, "").slice(0, 20);
    let formatted = "";
    for (let i = 0; i < digits.length; i++) {
      if (i === 3 || i === 6) formatted += " - ";
      formatted += digits[i];
    }
    return formatted;
  };

  // ✅ ZIP code validation handler
  const handlePostalBlur = async (zipValue) => {
    const zip = zipValue;
    if (!zip) return;

    const stateid = addUserFormData.state;
    const countyid = addUserFormData.county;

    try {
      const res = await api.post(
        "/auth/verify-zip",
        { zipcode: zip, maincompanyid: selectedMain, stateid, countyid }
      );
      if (res.data?.status === "fail") {
        setAddUserErrors(prev => ({ ...prev, postal_code: "Invalid ZIP code" }));
      } else {
        const state = res.data?.data?.stateid;
        const county = res.data?.data?.countyid;

        // Auto-populate state and county
        setAddUserFormData(prev => ({
          ...prev,
          state: state || prev.state,
          county: county || prev.county
        }));

        setAddUserErrors(prev => ({ ...prev, postal_code: undefined }));
      }
    } catch (err) {
      console.error("ZIP validation error:", err);
      setAddUserErrors(prev => ({ ...prev, postal_code: "ZIP code validation failed" }));
    }
  };

  // ✅ Phone change handler with formatting
  const handlePhoneChange = (value) => {
    const formatted = formatMobile(value);
    setAddUserFormData(prev => ({ ...prev, phone: formatted }));
  };

  const handleEditPhoneChange = (value) => {
    const formatted = formatMobile(value);
    setEditAdditionalData((prev) => ({ ...prev, phone: formatted }));
  };

  const handleEditPostalBlur = async (zipValue) => {
    const zip = zipValue;
    if (!zip) return;

    const stateid = editAdditionalData.state;
    const countyid = editAdditionalData.county;
    try {
      const res = await api.post("/auth/verify-zip", {
        zipcode: zip,
        maincompanyid: selectedMain,
        stateid,
        countyid,
      });
      if (res.data?.status === "fail") {
        setEditFormErrors((prev) => ({
          ...prev,
          postal_code: "Invalid ZIP code",
        }));
      } else {
        const state = res.data?.data?.stateid;
        const county = res.data?.data?.countyid;
        setEditAdditionalData((prev) => ({
          ...prev,
          state: state ? String(state) : prev.state,
          county: county ? String(county) : prev.county,
        }));
        clearEditFieldError("postal_code");
        clearEditFieldError("state");
        clearEditFieldError("county");
      }
    } catch (err) {
      console.error("ZIP validation error:", err);
      setEditFormErrors((prev) => ({
        ...prev,
        postal_code: "ZIP code validation failed",
      }));
    }
  };

  // ✅ Email blur validation (same as Register.js)
  const handleAddUserEmailBlur = useCallback(async (eOrValue) => {
    const email = typeof eOrValue === "string" ? eOrValue : eOrValue?.target?.value;
    if (!email || !selectedMain) return;
    try {
      const res = await api.get("/auth/check-email", {
        params: { email, maincompanyid: selectedMain, user_type: addUserFormData.user_type },
      });
      if (res.data?.status !== "success") {
        addUserEmailValidRef.current = false;
        setAddUserErrors(prev => ({ ...prev, email: "Email already exists." }));
      } else {
        addUserEmailValidRef.current = true;
        setAddUserErrors(prev => {
          const next = { ...prev };
          delete next.email;
          return next;
        });
      }
    } catch {
      addUserEmailValidRef.current = false;
      setAddUserErrors(prev => ({ ...prev, email: "Email validation failed. Please try again." }));
    }
  }, [selectedMain, addUserFormData]);

  // ✅ Username blur validation (same as Register.js)
  const handleAddUserUsernameBlur = useCallback(async (eOrValue) => {
    const username = typeof eOrValue === "string" ? eOrValue : eOrValue?.target?.value;
    if (!username || !selectedMain) return;
    try {
      const res = await api.get("/auth/check-username", {
        params: { username, maincompanyid: selectedMain },
      });
      if (res.data?.status !== "success") {
        addUserUsernameValidRef.current = false;
        setAddUserErrors(prev => ({ ...prev, username: "Username already exists." }));
      } else {
        addUserUsernameValidRef.current = true;
        setAddUserErrors(prev => {
          const next = { ...prev };
          delete next.username;
          return next;
        });
      }
    } catch {
      addUserUsernameValidRef.current = false;
      setAddUserErrors(prev => ({ ...prev, username: "Username validation failed. Please try again." }));
    }
  }, [selectedMain]);

  const handleAddUserEmailChange = useCallback(() => {
    addUserEmailValidRef.current = null;
    setAddUserErrors(prev => {
      const next = { ...prev };
      delete next.email;
      return next;
    });
  }, []);

  const handleAddUserUsernameChange = useCallback(() => {
    addUserUsernameValidRef.current = null;
    setAddUserErrors(prev => {
      const next = { ...prev };
      delete next.username;
      return next;
    });
  }, []);

  // UI pieces
  if (loading) {
    return (
      <div className="p-4">
        <AdminPageHeader
          title="Manage Users"
          actions={<div className="h-10 w-32 bg-gray-200 animate-pulse rounded-lg"></div>}
          filters={<div className="h-12 w-full bg-gray-200 animate-pulse rounded-lg"></div>}
        />

        {/* Loading Table Skeleton */}
        <div className="bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-4 w-12">
                    <div className="h-4 w-4 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                  <th className="px-4 py-4 min-w-[200px]">
                    <div className="h-4 w-32 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                  <th className="px-4 py-4 min-w-[120px] hidden sm:table-cell">
                    <div className="h-4 w-24 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                  <th className="px-4 py-4 min-w-[100px]">
                    <div className="h-4 w-20 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                  <th className="px-4 py-4 min-w-[180px] hidden md:table-cell">
                    <div className="h-4 w-28 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                  <th className="px-4 py-4 min-w-[100px]">
                    <div className="h-4 w-16 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                  <th className="px-4 py-4 min-w-[120px] hidden lg:table-cell">
                    <div className="h-4 w-20 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                  <th className="px-4 py-4 min-w-[100px] hidden xl:table-cell">
                    <div className="h-4 w-24 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-4 py-4">
                      <div className="h-4 w-4 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex gap-1">
                          <div className="h-3 w-3 bg-gray-200 animate-pulse rounded-full"></div>
                          <div className="h-4 w-16 bg-gray-200 animate-pulse rounded-full"></div>
                        </div>
                        <div className="h-4 w-32 bg-gray-200 animate-pulse rounded"></div>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden sm:table-cell">
                      <div className="h-4 w-24 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="h-4 w-20 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <div className="h-4 w-28 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="h-4 w-16 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      <div className="h-4 w-20 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td className="px-4 py-4 hidden xl:table-cell">
                      <div className="h-4 w-24 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="p-4">
      <AdminPageHeader
        title="Manage Users"
        actions={(
          <>
            {/* Bulk toolbar shown in original page when selected.length > 0 */}
            {selected.length > 0 && (
              <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-600">{selected.length} selected</span>

                {/* Assign Rep */}
                <div className="relative group">
                  <Briefcase
                    className="w-5 h-5 text-blue-600 cursor-pointer"
                    onClick={() => {
                      if (selected.length !== 1) {
                        showInfoModal(
                          "Selection required",
                          "Please select exactly one user to assign a Sales Rep."
                        );
                        return;
                      }
                      setModal({ type: "assign", data: selected });
                    }}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap 
                     bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 
                     group-hover:opacity-100 transition">
                    Assign Rep
                  </span>
                </div>

                {/* Send Email */}
                <div className="relative group">
                  <Send
                    className="w-5 h-5 text-indigo-600 cursor-pointer"
                    onClick={() => {
                      if (selected.length !== 1) {
                        showInfoModal(
                          "Selection required",
                          "Please select exactly one user to send an email."
                        );
                        return;
                      }
                      setModal({ type: "sendEmail", data: selected });
                    }}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap 
                     bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 
                     group-hover:opacity-100 transition">
                    Send Email
                  </span>
                </div>

                {/* Disable User */}
                <div className="relative group">
                  <ShieldOff
                    className="w-5 h-5 text-yellow-600 cursor-pointer"
                    onClick={() => {
                      if (selected.length !== 1) {
                        showInfoModal(
                          "Selection required",
                          "Please select exactly one user to reset the password."
                        );
                        return;
                      }
                      setModal({ type: "disable", data: selected });
                    }}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap 
                     bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 
                     group-hover:opacity-100 transition">
                    {selected[0]?.is_active ? "Disable User" : "Activate User"}
                  </span>
                </div>

                {/* Reset Password */}
                <div className="relative group">
                  <KeyRound
                    className="w-5 h-5 text-orange-600 cursor-pointer"
                    onClick={() => {
                      if (selected.length !== 1) {
                        showInfoModal(
                          "Selection required",
                          "Please select exactly one user to reset the password."
                        );
                        return;
                      }
                      setModal({ type: "reset", data: selected });
                    }}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap 
                     bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 
                     group-hover:opacity-100 transition">
                    Reset Password
                  </span>
                </div>

                {/* Edit */}
                <div className="relative group">
                  <Pencil
                    className="w-5 h-5 text-green-600 cursor-pointer"
                    onClick={() => {
                      if (selected.length !== 1) {
                        showInfoModal(
                          "Selection required",
                          "Please select exactly one user to edit."
                        );
                        return;
                      }
                      setModal({ type: "edit", data: selected });
                    }}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap 
                     bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 
                     group-hover:opacity-100 transition">
                    Edit
                  </span>
                </div>

                {/* Delete */}
                <div className="relative group">
                  <Trash
                    className="w-5 h-5 text-red-600 cursor-pointer"
                    onClick={() => setModal({ type: "delete", data: selected })}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap 
                     bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 
                     group-hover:opacity-100 transition">
                    Delete
                  </span>
                </div>

                {/* Approve & Reject (only if pending) */}
                {selected[0].user_status === "pending" && (
                  <>
                    <div className="relative group">
                      <CheckCircle
                        className="w-5 h-5 text-green-600 cursor-pointer"
                        onClick={() => setModal({ type: "approve", data: selected })}
                      />
                      <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap 
                         bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 
                         group-hover:opacity-100 transition">
                        Approve User
                      </span>
                    </div>

                    <div className="relative group">
                      <XCircle
                        className="w-5 h-5 text-red-600 cursor-pointer"
                        onClick={() => setModal({ type: "reject", data: selected })}
                      />
                      <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap 
                         bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 
                         group-hover:opacity-100 transition">
                        Reject User
                      </span>
                    </div>
                  </>
                )}
              </div>

            )}

            {/* Modern Action Buttons */}
            <div className="flex flex-wrap gap-3">
              {/* Add New User button */}
              <button
                onClick={() => {
                  addUserEmailValidRef.current = null;
                  addUserUsernameValidRef.current = null;
                  setAddUserErrors({});
                  setFormError("");
                  setAddUserFormData({
                    user_type: "Normal User",
                    first_name: "",
                    last_name: "",
                    email: "",
                    username: "",
                    password: "",
                    mailing_address: "",
                    city: "",
                    state: "",
                    county: "",
                    postal_code: "",
                    phone: "",
                    office_name: "",
                    titlerep: "",
                    dre_nmls: "",
                    corporate_nmls: ""
                  });
                  setModal({ type: "addUser", data: null });
                }}
                className={`inline-flex items-center gap-2 px-4 py-2.5 ${primaryBtnClasses} text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transform hover:scale-[1.02] transition-all duration-200 focus:ring-2 focus:ring-[var(--role-border-primary)] focus:ring-offset-2`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline">Add New User</span>
                <span className="sm:hidden">Add User</span>
              </button>

              {/* Export button */}
              <button
                disabled={meta.total === 0}
                onClick={handleExport}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg shadow-sm transform transition-all duration-200 focus:ring-2 focus:ring-offset-2 ${meta.total === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:shadow-md hover:scale-[1.02] focus:ring-green-500'
                  }`}
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span className="hidden sm:inline">Export Excel</span>
                <span className="sm:hidden">Export</span>
              </button>
            </div>
          </>
        )}
        filters={(
          <div className="flex flex-wrap items-stretch gap-4 w-full">
            {/* Left: filter form */}
            <div className="flex-1 min-w-[300px]">
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                  {/* 1. Search input (Search button applies all filters below) */}
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Search name, email or username
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search name, email or username..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors bg-gray-50 focus:bg-white"
                      />
                      <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>

                  {/* 2. User Type */}
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      User Type
                    </label>
                    <select
                      value={filters.userType}
                      onChange={(e) => setFilters((f) => ({ ...f, userType: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors bg-white"
                    >
                      <option value="">All User Types</option>
                      <option value="N">Normal User</option>
                      <option value="M">Master Account</option>
                    </select>
                  </div>

                  {/* 3. Created Date Range */}
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Created Date Range
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        value={filters.createdFrom}
                        placeholder="From"
                        onChange={(e) =>
                          setFilters((f) => ({ ...f, createdFrom: e.target.value }))
                        }
                        className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors bg-white"
                      />
                      <input
                        type="date"
                        value={filters.createdTo}
                        placeholder="To"
                        onChange={(e) =>
                          setFilters((f) => ({ ...f, createdTo: e.target.value }))
                        }
                        className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors bg-white"
                      />
                    </div>
                  </div>

                  {/* 4. Location Filters */}
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Location
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={selectedState}
                        onChange={(e) => setSelectedState(Number(e.target.value) || 0)}
                        className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors bg-white"
                      >
                        <option value={0}>All States</option>
                        {states.map((s) => (
                          <option key={s.id || s.stateid || s.value} value={s.id || s.stateid || s.value}>{s.statename || s.name}</option>
                        ))}
                      </select>
                      <select
                        value={selectedCounty}
                        onChange={(e) => setSelectedCounty(Number(e.target.value) || 0)}
                        className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors bg-white"
                      >
                        <option value={0}>All Counties</option>
                        {counties.map((c) => (
                          <option key={c.id || c.countyid || c.value} value={c.id || c.countyid || c.value}>{c.countyname || c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* 5. Title Rep */}
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Title Rep
                    </label>
                    <select
                      value={selectedTitleRep}
                      onChange={(e) => setSelectedTitleRep(Number(e.target.value) || 0)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors bg-white"
                    >
                      <option value={0}>All Title Reps</option>
                      {titlereps.map((t) => (
                        <option key={t.id || t.value} value={t.id || t.value}>{t.name || t.label || t.fullname}</option>
                      ))}
                    </select>
                  </div>

                  {/* 6. Search button – applies all filters above */}

                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Search
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="">
                        <button
                          onClick={() => {
                            setPage(1);
                            setTimeout(() => fetchUsers(), 0);
                          }}
                          className={`inline-flex items-center gap-2 px-6 py-2.5 ${primaryBtnClasses} text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transform hover:scale-[1.02] transition-all duration-200 focus:ring-2 focus:ring-[var(--role-border-primary)] focus:ring-offset-2`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <span className="hidden sm:inline">Search</span>
                        </button>
                      </div>

                      {/* 7. Reset Button */}
                      <div className="">
                        <button
                          onClick={() => {
                            // reset local filters
                            setFilters({ query: "", createdFrom: "", createdTo: "", userType: "" });
                            setSearchQuery("");
                            setSelectedState(0);
                            setSelectedCounty(0);
                            setSelectedTitleRep(0);
                            setUrlUserId("");

                            // remove userId from the URL while preserving other params
                            try {
                              const params = new URLSearchParams(location.search || "");
                              params.delete('userId');
                              const newSearch = params.toString();
                              const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
                              navigate(newUrl, { replace: true });
                            } catch (e) {
                              // ignore URL update errors
                            }
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-[var(--role-border-primary)] focus:ring-offset-2 transition-colors"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Reset Filters
                        </button>
                      </div>
                    </div>
                  </div>


                </div>
              </div>
            </div>
            {/* Right: stats (column layout to free space for filters) */}
            <div className="flex flex-col gap-2 flex-shrink-0">
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5 shadow-sm flex items-center gap-2 min-w-[140px]">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 truncate">Total Users</p>
                  <p className="text-base font-bold text-gray-900">{meta.total}</p>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5 shadow-sm flex items-center gap-2 min-w-[140px]">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 truncate">Active Users</p>
                  <p className="text-base font-bold text-green-600">{paginatedUsers.filter(u => u.is_active).length}</p>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5 shadow-sm flex items-center gap-2 min-w-[140px]">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 truncate">Filtered Results</p>
                  <p className="text-base font-bold text-purple-600">{paginatedUsers.length}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      />

      {/* Enhanced Table */}
      <div className="bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-4 py-4 text-left w-12">
                  <input
                    type="checkbox"
                    checked={
                      selected.length === paginatedUsers.length &&
                      paginatedUsers.length > 0
                    }
                    onChange={toggleSelectAll}
                    className="w-4 h-4 bg-gray-100 border-gray-300 rounded focus:ring-2 focus:ring-[var(--role-border-primary)]"
                  />
                </th>
                <th className="px-4 py-4 text-left min-w-[200px]">
                  <span className="font-semibold text-gray-900 text-sm">Name & Status</span>
                </th>
                <th className="px-4 py-4 text-left min-w-[120px] hidden sm:table-cell">
                  <span className="font-semibold text-gray-900 text-sm">Company</span>
                </th>
                <th className="px-4 py-4 text-left min-w-[100px]">
                  <span className="font-semibold text-gray-900 text-sm">Username</span>
                </th>
                <th className="px-4 py-4 text-left min-w-[180px] hidden md:table-cell">
                  <span className="font-semibold text-gray-900 text-sm">Email/Phone</span>
                </th>
                <th className="px-4 py-4 text-left min-w-[100px]">
                  <span className="font-semibold text-gray-900 text-sm">Type</span>
                </th>
                <th className="px-4 py-4 text-left min-w-[120px] hidden lg:table-cell">
                  <span className="font-semibold text-gray-900 text-sm">Sales Rep</span>
                </th>
                <th className="px-4 py-4 text-left min-w-[100px] hidden xl:table-cell">
                  <span className="font-semibold text-gray-900 text-sm">Created</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <div className="text-center">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Users Found</h3>
                        <p className="text-gray-600 mb-4">
                          {searchQuery || filters.userType || selectedState || selectedCounty || selectedTitleRep
                            ? "Try adjusting your search criteria or filters."
                            : "Get started by creating your first user."
                          }
                        </p>
                        {(searchQuery || filters.userType || selectedState || selectedCounty || selectedTitleRep) && (
                          <button
                            onClick={() => {
                              setFilters({ query: "", createdFrom: "", createdTo: "", userType: "" });
                              setSearchQuery("");
                              setSelectedState(0);
                              setSelectedCounty(0);
                              setSelectedTitleRep(0);
                              setUrlUserId("");
                            }}
                            className={`inline-flex items-center gap-2 px-4 py-2 ${primaryBtnClasses} rounded-lg hover:opacity-90 transition-colors font-medium`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Clear Filters
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((u, idx) => {
                  const isSelected = !!selected.find((item) => item.id === u.id);
                  return (
                    <tr
                      key={u.id}
                      className={`
                        transition-all duration-200 ease-in-out
                        ${isSelected
                          ? "bg-[var(--role-border-light)]/20 border-l-4 border-[var(--role-border-primary)] shadow-sm"
                          : idx % 2 === 0
                            ? "bg-white hover:bg-gray-50"
                            : "bg-gray-25 hover:bg-gray-50"
                        }
                        ${u.user_status === "pending" ? "bg-yellow-25 border-l-4 border-yellow-400" : ""}
                        ${u.user_status === "rejected" ? "bg-red-25 border-l-4 border-red-400" : ""}
                        border-b border-gray-100
                      `}
                    >
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(u)}
                          className="w-4 h-4 bg-gray-100 border-gray-300 rounded focus:ring-2 focus:ring-[var(--role-border-primary)]"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3 min-w-0">
                          {/* Profile avatar */}
                          <div className="flex-shrink-0 relative">
                            {u.profile_pic ? (
                              <img
                                src={`${process.env.REACT_APP_ASSETS_BASE_URL}/uploads/users/${u.profile_pic}?t=${Date.now()}`}
                                alt=""
                                className="w-10 h-10 rounded-full object-cover border border-gray-200 bg-gray-50 ring-1 ring-gray-100"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const fallback = e.target.nextElementSibling;
                                  if (fallback) fallback.classList.remove('hidden');
                                }}
                              />
                            ) : null}
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center border border-gray-200 bg-gray-100 ${u.profile_pic ? 'hidden' : ''}`}
                              aria-hidden
                            >
                              <User className="w-5 h-5 text-gray-400" />
                            </div>
                            {/* Active status ring */}
                            <span
                              className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${u.is_active ? 'bg-green-500' : 'bg-gray-400'}`}
                              title={u.is_active ? 'Active' : 'Inactive'}
                            />
                          </div>
                          {/* Name + badges */}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 text-sm truncate">
                              {formatName(u.name)}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap mt-0.5">
                              {u.user_status === "pending" && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-amber-50 text-amber-800 border border-amber-200">
                                  Pending
                                </span>
                              )}
                              {u.user_status === "rejected" && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-red-50 text-red-800 border border-red-200">
                                  Rejected
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4 hidden sm:table-cell">
                        <span className="text-sm font-medium text-gray-900">{u.company}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm font-mono bg-gray-50 px-2 py-1 rounded border text-gray-700">
                          {u.username}
                        </span>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <span className="text-sm text-gray-700 truncate block" title={u.email}>
                          {u.email}
                          <br />
                          <a href={`tel:${u.phone}`} className="text-blue-500">{u.phone || "—"}</a>
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${u.user_type === "M"
                          ? "bg-purple-100 text-purple-800 border border-purple-200" : u.user_type === "SM"
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : u.user_type === "N"
                              ? "bg-[var(--role-border-light)]/30 text-gray-800 border border-[var(--role-border-light)]"
                              : "bg-gray-100 text-gray-800 border border-gray-200"
                          }`}>
                          {u.user_type === "SM" && "👤"}
                          {u.user_type === "M" && "⚡"}
                          {u.user_type === "N" && "👤"}
                          {u.user_type === "M" ? "M" : u.user_type === "N" ? "N" : u.user_type || "?"}
                        </span>
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        <span className="text-sm text-gray-900">
                          {formatName(u.titleRep || "Unassigned")}
                        </span>
                      </td>
                      <td className="px-4 py-4 hidden xl:table-cell">
                        <span className="text-sm text-gray-600">
                          {u.created}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {meta.total > 0 && (
        <div className="flex flex-wrap justify-between items-center gap-3 mt-3 text-sm">
          <span>
            Showing {showingFrom}–{showingTo} of {meta.total}
          </span>

          <div className="flex items-center gap-2">
            <label className="text-gray-600">Rows per page</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="border rounded px-2 py-1"
            >
              {[10, 25, 50].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <nav className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={`px-3 py-1 border rounded ${currentPage === n
                  ? primaryBtnClasses
                  : "hover:bg-gray-100"
                  }`}
              >
                {n}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
            >
              Next
            </button>
          </nav>
        </div>
      )}




      {/* ✅ Double Confirmation Modals */}
      {(modal.type && modalConfig[modal.type]) && (() => {
        const rawConfig = modalConfig[modal.type];
        const config = typeof rawConfig === "function" ? rawConfig(modal.data) : rawConfig;

        if (!config) return null; // safety guard

        return (
          <Modal
            title={config.title}
            size={
              step === "final" && ["edit", "assign", "sendEmail", "addUser"].includes(modal.type)
                ? "4xl"
                : "md"
            }
            style={
              step === "final" && ["edit", "addUser"].includes(modal.type)
                ? { maxHeight: '90vh', height: 'auto' }
                : {}
            }
            onClose={() => {
              if (modal.type === "addUser") {
                setAddUserErrors({});
                setFormError("");
                addUserEmailValidRef.current = null;
                addUserUsernameValidRef.current = null;
              }
              setEditUserProfilePreviewOpen(false);
              setModal({ type: null, data: null });
              setStep("confirm");
              setActionLoading(false);
            }}
          >
            <p>{step === "confirm" ? config.confirm : config.final}</p>

            {/* Extra UI */}
            {modal.type === "assign" && step === "final" && (
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Select Representative
                </label>
                <select
                  value={selectedRep}
                  onChange={(e) => setSelectedRep(e.target.value)}
                  className="w-full border rounded p-2"
                >
                  <option value="">-- Choose a Rep --</option>
                  {reps.map((rep) => (
                    <option key={rep.id} value={rep.id}>
                      {rep.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {modal.type === "sendEmail" && step === "final" && (
              <div className="mt-4">
                <input
                  type="text"
                  value={sendEmail}
                  onChange={(e) => setSendEmail(e.target.value)}
                  className="w-full border rounded p-2"
                  placeholder="Enter email address..."
                />
              </div>
            )}

            {modal.type === "edit" && step === "final" && (() => {
              const selectedUser = modal.data?.[0];
              const baseUser = selectedUser ? (users.find((u) => u.id === selectedUser.id) || selectedUser) : selectedUser;
              // Prefer editUserDetails (full API response) when available for city, mailing_address, etc.
              const editUser = editUserDetails ? { ...baseUser, ...editUserDetails } : baseUser;
              return (
                <div className="space-y-6">
                  {formError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 text-red-500 flex-shrink-0">⚠️</div>
                        <p className="text-sm text-red-700 font-medium">{formError}</p>
                      </div>
                    </div>
                  )}

                  {/* Profile Picture & User Name Header */}
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-shrink-0 relative" data-edit-user-avatar>
                      {(editUser?.profile_pic ?? editUser?.profilePic) ? (
                        <button
                          type="button"
                          onClick={() => {
                            setEditUserProfilePicBust(Date.now());
                            setEditUserProfilePreviewOpen(true);
                          }}
                          className="group relative rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--role-border-primary)] focus-visible:ring-offset-2"
                          aria-label="View profile picture preview"
                        >
                          <img
                            src={`${process.env.REACT_APP_ASSETS_BASE_URL}/uploads/users/${editUser.profile_pic ?? editUser.profilePic}?t=${Date.now()}`}
                            alt={`${editFirstName} ${editLastName}`.trim() || 'Profile'}
                            className="w-20 h-20 rounded-full object-cover border-2 border-gray-200 shadow-sm transition group-hover:brightness-95"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const btn = e.target.closest('button');
                              if (btn) btn.style.display = 'none';
                              const container = e.target.closest('[data-edit-user-avatar]');
                              const fallback = container?.querySelector('[data-user-profile-fallback]');
                              if (fallback) fallback.classList.remove('hidden');
                            }}
                          />
                          <span className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center pointer-events-none">
                            <ZoomIn className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md" aria-hidden />
                          </span>
                        </button>
                      ) : null}
                      <div
                        data-user-profile-fallback
                        className={`w-20 h-20 rounded-full flex items-center justify-center border-2 border-gray-200 bg-gray-100 ${(editUser?.profile_pic ?? editUser?.profilePic) ? 'hidden' : ''}`}
                        aria-hidden
                      >
                        <User className="w-10 h-10 text-gray-400" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {`${editFirstName} ${editLastName}`.trim() || 'User'}
                      </h3>
                      {editUser?.email && (
                        <p className="text-sm text-gray-600 mt-0.5 truncate">{editUser.email}</p>
                      )}
                    </div>
                  </div>

                  {/* Personal Information Section */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <UserCheck className="w-4 h-4 text-blue-600" />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-800">Personal Information</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={editFirstName}
                          onChange={(e) => {
                            setEditFirstName(e.target.value);
                            clearEditFieldError("first_name");
                          }}
                          className={getEditInputClass(
                            "first_name",
                            editFormErrors,
                            "focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]",
                          )}
                          placeholder="Enter first name"
                        />
                        {editFormErrors.first_name && (
                          <p className="text-red-500 text-xs mt-1">{editFormErrors.first_name}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={editLastName}
                          onChange={(e) => {
                            setEditLastName(e.target.value);
                            clearEditFieldError("last_name");
                          }}
                          className={getEditInputClass(
                            "last_name",
                            editFormErrors,
                            "focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]",
                          )}
                          placeholder="Enter last name"
                        />
                        {editFormErrors.last_name && (
                          <p className="text-red-500 text-xs mt-1">{editFormErrors.last_name}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Contact Information Section */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <Mail className="w-4 h-4 text-green-600" />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-800">Contact Information</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={editEmail}
                          onChange={(e) => {
                            setEditEmail(e.target.value);
                            clearEditFieldError("email");
                          }}
                          className={getEditInputClass(
                            "email",
                            editFormErrors,
                            "focus:ring-green-500 focus:border-green-500",
                          )}
                          placeholder="Enter email address"
                        />
                        {editFormErrors.email && (
                          <p className="text-red-500 text-xs mt-1">{editFormErrors.email}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          Confirm Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={editConfirmEmail}
                          onChange={(e) => {
                            setEditConfirmEmail(e.target.value);
                            clearEditFieldError("confirm_email");
                          }}
                          className={getEditInputClass(
                            "confirm_email",
                            editFormErrors,
                            "focus:ring-green-500 focus:border-green-500",
                          )}
                          placeholder="Confirm email address"
                        />
                        {editFormErrors.confirm_email && (
                          <p className="text-red-500 text-xs mt-1">{editFormErrors.confirm_email}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-slate-600" />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-800">Additional Information</h3>
                    </div>
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-500">Company</label>
                        <p className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded-lg border border-gray-100">
                          {editUser?.company_name ?? editUser?.company ?? "—"}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          Business Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={editAdditionalData.mailing_address}
                          onChange={(e) => {
                            setEditAdditionalData((prev) => ({
                              ...prev,
                              mailing_address: e.target.value,
                            }));
                            clearEditFieldError("mailing_address");
                          }}
                          className={getEditInputClass(
                            "mailing_address",
                            editFormErrors,
                            "focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]",
                          )}
                          placeholder="Enter street address"
                        />
                        {editFormErrors.mailing_address && (
                          <p className="text-red-500 text-xs mt-1">{editFormErrors.mailing_address}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-700">
                            State <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={editAdditionalData.state}
                            onChange={(e) => {
                              setEditAdditionalData((prev) => ({
                                ...prev,
                                state: e.target.value,
                                county: "",
                                titlerep: "",
                              }));
                              setEditUserCounties([]);
                              setEditUserTitleReps([]);
                              clearEditFieldError("state");
                              clearEditFieldError("county");
                            }}
                            className={getEditInputClass(
                              "state",
                              editFormErrors,
                              "focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]",
                            )}
                          >
                            {editUserStates.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.statename}
                              </option>
                            ))}
                          </select>
                          {editFormErrors.state && (
                            <p className="text-red-500 text-xs mt-1">{editFormErrors.state}</p>
                          )}
                        </div>

                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-700">
                            County <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={editAdditionalData.county}
                            onChange={(e) => {
                              setEditAdditionalData((prev) => ({
                                ...prev,
                                county: e.target.value,
                                titlerep: "",
                              }));
                              setEditUserTitleReps([]);
                              clearEditFieldError("county");
                            }}
                            className={getEditInputClass(
                              "county",
                              editFormErrors,
                              "focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]",
                            )}
                            disabled={!editAdditionalData.state || isLoadingEditCounties}
                          >
                            {editUserCounties.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.countyname}
                              </option>
                            ))}
                          </select>
                          {editFormErrors.county && (
                            <p className="text-red-500 text-xs mt-1">{editFormErrors.county}</p>
                          )}
                        </div>

                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-700">
                            City <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={editAdditionalData.city}
                            onChange={(e) => {
                              setEditAdditionalData((prev) => ({
                                ...prev,
                                city: e.target.value,
                              }));
                              clearEditFieldError("city");
                            }}
                            className={getEditInputClass(
                              "city",
                              editFormErrors,
                              "focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]",
                            )}
                            placeholder="Enter city"
                          />
                          {editFormErrors.city && (
                            <p className="text-red-500 text-xs mt-1">{editFormErrors.city}</p>
                          )}
                        </div>

                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-700">
                            ZIP Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={editAdditionalData.postal_code}
                            onChange={(e) => {
                              setEditAdditionalData((prev) => ({
                                ...prev,
                                postal_code: e.target.value,
                              }));
                              clearEditFieldError("postal_code");
                            }}
                            onBlur={(e) => handleEditPostalBlur(e.target.value)}
                            className={getEditInputClass(
                              "postal_code",
                              editFormErrors,
                              "focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]",
                            )}
                            placeholder="Enter ZIP code"
                            maxLength={5}
                          />
                          {editFormErrors.postal_code && (
                            <p className="text-red-500 text-xs mt-1">{editFormErrors.postal_code}</p>
                          )}
                        </div>

                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-700">
                            Phone <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={editAdditionalData.phone}
                            onChange={(e) => {
                              handleEditPhoneChange(e.target.value);
                              clearEditFieldError("phone");
                            }}
                            className={getEditInputClass(
                              "phone",
                              editFormErrors,
                              "focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]",
                            )}
                            placeholder="Enter phone number"
                          />
                          {editFormErrors.phone && (
                            <p className="text-red-500 text-xs mt-1">{editFormErrors.phone}</p>
                          )}
                        </div>

                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-700">
                            Title Rep
                          </label>
                          <select
                            value={editAdditionalData.titlerep}
                            onChange={(e) =>
                              setEditAdditionalData((prev) => ({
                                ...prev,
                                titlerep: e.target.value,
                              }))
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                            disabled={!editAdditionalData.county}
                          >
                            {editUserTitleReps.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-1 md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700">
                            Brokerage Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={editAdditionalData.office_name}
                            onChange={(e) => {
                              setEditAdditionalData((prev) => ({
                                ...prev,
                                office_name: e.target.value,
                              }));
                              clearEditFieldError("office_name");
                            }}
                            className={getEditInputClass(
                              "office_name",
                              editFormErrors,
                              "focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]",
                            )}
                            placeholder="Enter brokerage name"
                          />
                          {editFormErrors.office_name && (
                            <p className="text-red-500 text-xs mt-1">{editFormErrors.office_name}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>



                  {/* Security Settings Section */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Lock className="w-4 h-4 text-orange-600" />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-800">Security Settings</h3>
                    </div>

                    <div className="space-y-4">
                      {/* Password Change */}
                      <div className="border border-gray-100 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-3">
                          <input
                            type="checkbox"
                            id="changePassword"
                            checked={changePassword}
                            onChange={(e) => setChangePassword(e.target.checked)}
                            className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                          />
                          <label htmlFor="changePassword" className="text-sm font-medium text-gray-700 cursor-pointer">
                            Change Password
                          </label>
                        </div>

                        {changePassword && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-6">
                            <div className="space-y-1">
                              <label className="block text-xs font-medium text-gray-600">
                                New Password <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="password"
                                value={editPassword}
                                onChange={(e) => {
                                  setEditPassword(e.target.value);
                                  clearEditFieldError("password");
                                }}
                                className={getEditInputClass(
                                  "password",
                                  editFormErrors,
                                  "focus:ring-orange-500 focus:border-orange-500",
                                )}
                                placeholder="Enter new password"
                              />
                              {editFormErrors.password && (
                                <p className="text-red-500 text-xs mt-1">{editFormErrors.password}</p>
                              )}
                            </div>

                            <div className="space-y-1">
                              <label className="block text-xs font-medium text-gray-600">
                                Confirm Password <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="password"
                                value={editConfirmPassword}
                                onChange={(e) => {
                                  setEditConfirmPassword(e.target.value);
                                  clearEditFieldError("confirm_password");
                                }}
                                className={getEditInputClass(
                                  "confirm_password",
                                  editFormErrors,
                                  "focus:ring-orange-500 focus:border-orange-500",
                                )}
                                placeholder="Confirm new password"
                              />
                              {editFormErrors.confirm_password && (
                                <p className="text-red-500 text-xs mt-1">{editFormErrors.confirm_password}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Username Change */}
                      <div className="border border-gray-100 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-3">
                          <input
                            type="checkbox"
                            id="changeUsername"
                            checked={changeUsername}
                            onChange={(e) => setChangeUsername(e.target.checked)}
                            className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                          />
                          <label htmlFor="changeUsername" className="text-sm font-medium text-gray-700 cursor-pointer">
                            Change Username
                          </label>
                        </div>

                        {changeUsername && (
                          <div className="ml-6 space-y-1">
                            <label className="block text-xs font-medium text-gray-600">
                              New Username <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={editUsername}
                              onChange={(e) => {
                                setEditUsername(e.target.value);
                                clearEditFieldError("username");
                              }}
                              className={getEditInputClass(
                                "username",
                                editFormErrors,
                                "focus:ring-orange-500 focus:border-orange-500",
                              )}
                              placeholder="Enter new username"
                            />
                            {editFormErrors.username && (
                              <p className="text-red-500 text-xs mt-1">{editFormErrors.username}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {modal.type === "addUser" && step === "final" && (
              <div className="space-y-6">
                {formError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 text-red-500 flex-shrink-0">⚠️</div>
                      <p className="text-sm text-red-700 font-medium">{formError}</p>
                    </div>
                  </div>
                )}

                {/* Company and User Type Information */}
                <div className="col-span-2 mb-6">
                  <div
                    className="border rounded-xl p-4 shadow-sm"
                    style={{
                      background: roleTheme?.gradientBg?.primary || 'linear-gradient(135deg, rgba(239, 246, 255, 0.95), rgba(238, 242, 255, 0.9))',
                      borderColor: roleTheme?.colors?.sidebar?.hover || '#bfdbfe'
                    }}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Company Information */}
                      <div className="flex items-start gap-3">
                        <div
                          className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{
                            backgroundColor: '#dbeafe',
                            color: '#2563eb'
                          }}
                        >
                          <Building2 className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p
                            className="text-xs font-medium uppercase tracking-wide"
                            style={{ color: roleTheme?.colors?.sidebar?.active || '#1d4ed8' }}
                          >
                            Company
                          </p>
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {mainCompanies.find(company => company.id === selectedMain)?.main_company_name || 'Unknown Company'}
                          </p>
                        </div>
                      </div>

                      {/* User Type Selection */}
                      <div className="flex items-start gap-3">
                        <div
                          className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{
                            backgroundColor: '#dcfce7',
                            color: '#16a34a'
                          }}
                        >
                          <User className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <label
                            className="block text-xs font-medium uppercase tracking-wide mb-1"
                            style={{ color: roleTheme?.colors?.accents?.success || '#15803d' }}
                          >
                            User Type
                          </label>
                          <select
                            value={addUserFormData.user_type}
                            onChange={(e) => setAddUserFormData(prev => ({ ...prev, user_type: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                          >
                            <option value="Normal User">Normal User</option>
                            <option value="Master Account">Master Account</option>
                          </select>
                          {addUserErrors.user_type && <p className="text-red-500 text-xs mt-1">{addUserErrors.user_type}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Personal Information Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <UserCheck className="w-4 h-4 text-blue-600" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800">Personal Information</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={addUserFormData.first_name}
                        onChange={(e) => setAddUserFormData(prev => ({ ...prev, first_name: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                        placeholder="Enter first name"
                      />
                      {addUserErrors.first_name && <p className="text-red-500 text-xs mt-1">{addUserErrors.first_name}</p>}
                    </div>

                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={addUserFormData.last_name}
                        onChange={(e) => setAddUserFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                        placeholder="Enter last name"
                      />
                      {addUserErrors.last_name && <p className="text-red-500 text-xs mt-1">{addUserErrors.last_name}</p>}
                    </div>
                  </div>
                </div>

                {/* Account Information Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Settings className="w-4 h-4 text-purple-600" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800">Account Information</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={addUserFormData.email}
                        onChange={(e) => {
                          setAddUserFormData(prev => ({ ...prev, email: e.target.value }));
                          handleAddUserEmailChange();
                        }}
                        onBlur={handleAddUserEmailBlur}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="Enter email address"
                      />
                      {addUserErrors.email && <p className="text-red-500 text-xs mt-1">{addUserErrors.email}</p>}
                    </div>

                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">
                        Username <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={addUserFormData.username}
                        onChange={(e) => {
                          setAddUserFormData(prev => ({ ...prev, username: e.target.value }));
                          handleAddUserUsernameChange();
                        }}
                        onBlur={handleAddUserUsernameBlur}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="Enter username"
                      />
                      {addUserErrors.username && <p className="text-red-500 text-xs mt-1">{addUserErrors.username}</p>}
                    </div>

                    <div className="space-y-1 md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700">
                        Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        value={addUserFormData.password}
                        onChange={(e) => setAddUserFormData(prev => ({ ...prev, password: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="Enter password"
                      />
                      {addUserErrors.password && <p className="text-red-500 text-xs mt-1">{addUserErrors.password}</p>}
                    </div>
                  </div>
                </div>

                {/* Address Information Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-yellow-600" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800">Address Information</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1 md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700">
                          Business Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={addUserFormData.mailing_address}
                          onChange={(e) => setAddUserFormData(prev => ({ ...prev, mailing_address: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
                          placeholder="Enter street address"
                        />
                        {addUserErrors.mailing_address && <p className="text-red-500 text-xs mt-1">{addUserErrors.mailing_address}</p>}
                      </div>

                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={addUserFormData.city}
                          onChange={(e) => setAddUserFormData(prev => ({ ...prev, city: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
                          placeholder="Enter city"
                        />
                        {addUserErrors.city && <p className="text-red-500 text-xs mt-1">{addUserErrors.city}</p>}
                      </div>

                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          ZIP Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={addUserFormData.postal_code}
                          onChange={(e) => setAddUserFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                          onBlur={(e) => handlePostalBlur(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
                          placeholder="Enter ZIP code"
                          maxLength={5}
                        />
                        {addUserErrors.postal_code && <p className="text-red-500 text-xs mt-1">{addUserErrors.postal_code}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          State <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={addUserFormData.state}
                          onChange={(e) => {
                            setAddUserFormData(prev => ({
                              ...prev,
                              state: e.target.value,
                              county: "",
                              titlerep: ""
                            }));
                            setAddUserCounties([]);
                            setAddUserTitleReps([]);
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
                        >
                          {addUserStates.map((s) => (
                            <option key={s.id} value={s.id}>{s.statename}</option>
                          ))}
                        </select>
                        {addUserErrors.state && <p className="text-red-500 text-xs mt-1">{addUserErrors.state}</p>}
                      </div>

                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          County <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={addUserFormData.county}
                          onChange={(e) => {
                            setAddUserFormData(prev => ({
                              ...prev,
                              county: e.target.value,
                              titlerep: ""
                            }));
                            setAddUserTitleReps([]);
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
                          disabled={!addUserFormData.state}
                        >
                          {addUserCounties.map((c) => (
                            <option key={c.id} value={c.id}>{c.countyname}</option>
                          ))}
                        </select>
                        {addUserErrors.county && <p className="text-red-500 text-xs mt-1">{addUserErrors.county}</p>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-4 h-4 text-cyan-600" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800">Contact Information</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={addUserFormData.phone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
                        placeholder="Enter phone number"
                      />
                      {addUserErrors.phone && <p className="text-red-500 text-xs mt-1">{addUserErrors.phone}</p>}
                    </div>

                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">
                        Brokerage Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={addUserFormData.office_name}
                        onChange={(e) => setAddUserFormData(prev => ({ ...prev, office_name: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
                        placeholder="Enter brokerage name"
                      />
                      {addUserErrors.office_name && <p className="text-red-500 text-xs mt-1">{addUserErrors.office_name}</p>}
                    </div>
                  </div>
                </div>

                {/* Professional Information Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-4 h-4 text-indigo-600" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800">Professional Information</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">
                        Title Representative
                      </label>
                      <select
                        value={addUserFormData.titlerep}
                        onChange={(e) => setAddUserFormData(prev => ({ ...prev, titlerep: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                        disabled={!addUserFormData.county}
                      >

                        {addUserTitleReps.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">
                        DRE Number
                      </label>
                      <input
                        type="text"
                        value={addUserFormData.dre_nmls}
                        onChange={(e) => setAddUserFormData(prev => ({ ...prev, dre_nmls: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                        placeholder="Enter DRE number"
                      />
                    </div>

                    <div className="space-y-1 md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700">
                        Corporate DRE Number
                      </label>
                      <input
                        type="text"
                        value={addUserFormData.corporate_nmls}
                        onChange={(e) => setAddUserFormData(prev => ({ ...prev, corporate_nmls: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                        placeholder="Enter corporate DRE number"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}


            {/* Buttons */}
            <div className="mt-6 flex justify-end gap-2">
              <button
                className="px-4 py-2 border rounded"
                onClick={() => {
                  if (modal.type === "addUser") {
                    setAddUserErrors({});
                    setFormError("");
                    addUserEmailValidRef.current = null;
                    addUserUsernameValidRef.current = null;
                  }
                  if (modal.type === "edit") {
                    setEditFormErrors({});
                    setFormError("");
                  }
                  setEditUserProfilePreviewOpen(false);
                  setModal({ type: null });
                  setStep("confirm");
                }}
              >
                Cancel
              </button>

              {step === "confirm" ? (
                <button
                  className={`px-4 py-2 ${primaryBtnClasses} rounded`}
                  onClick={() => setStep("final")}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Please wait...' : 'Next'}
                </button>
              ) : (
                <button
                  className={`px-4 py-2 rounded text-white ${config.button.color}`}
                  onClick={() => handleFinalAction(config.action)}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Processing...' : config.button.text}
                </button>
              )}
            </div>
          </Modal>
        );
      })()}

      {editUserProfilePreviewOpen && modal.type === 'edit' && step === 'final' && (() => {
        const u = getEditFlowUser();
        const pic = u?.profile_pic ?? u?.profilePic;
        if (!pic) return null;
        return (
          <Modal
            title="Profile picture"
            size="2xl"
            maxHeight="max-h-[90vh] overflow-auto"
            overlayClassName="z-[60]"
            onClose={() => setEditUserProfilePreviewOpen(false)}
          >
            <div className="flex justify-center">
              <img
                src={`${process.env.REACT_APP_ASSETS_BASE_URL}/uploads/users/${pic}?t=${editUserProfilePicBust}`}
                alt={`${editFirstName} ${editLastName}`.trim() || 'Profile preview'}
                className="max-h-[75vh] w-full max-w-full rounded-lg object-contain shadow-md border border-gray-100 bg-gray-50"
              />
            </div>
          </Modal>
        );
      })()}

      {/* Informational modal used instead of alert() */}
      {modal.type === 'info' && (
        <Modal title={modal.data?.title || 'Info'} onClose={() => setModal({ type: null, data: null })}>
          <div>
            <p className="text-gray-700">{modal.data?.message}</p>
            <div className="mt-4 flex justify-end">
              <button className={`px-3 py-1 ${primaryBtnClasses} rounded`} onClick={() => setModal({ type: null, data: null })}>OK</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Global toast */}
      {toast && (
        <Toast message={toast.message} type={toast.type} visible={!!toast.visible} />
      )}

    </div>
  );
}
